import { useState, useEffect } from "react";
import "./App.css";
import { motion, AnimatePresence } from "framer-motion";
import {
  Truck,
  Buildings,
  MapPin,
  Clock,
  CheckCircle,
  ArrowRight,
  List,
  X,
  ChartLineUp,
  Users,
  ShieldCheck,
  Lightning,
  Warehouse,
  CaretLeft,
  CaretRight,
  LinkedinLogo,
  TwitterLogo,
  InstagramLogo,
  Globe,
  WhatsappLogo,
} from "@phosphor-icons/react";
import axios from "axios";

// Company Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_dev-stack-guide-9/artifacts/lmrfkmnm_ChatGPT%20Image%20Mar%2031%2C%202026%2C%2010_35_21%20AM.png";

// WhatsApp Business Configuration
const WHATSAPP_NUMBER = "919881622323";
const WHATSAPP_MESSAGE = "Hi, I'm interested in your distribution services. Please share more details.";

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#services", label: "Services" },
    { href: "#why-us", label: "Why Us" },
    { href: "#pricing", label: "Pricing" },
    { href: "#coverage", label: "Coverage" },
    { href: "#contact", label: "Contact" },
  ];

  return (
    <motion.header
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass shadow-sm" : "bg-transparent"
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <nav className="flex items-center justify-between h-20">
          <a href="/" className="flex items-center gap-3" data-testid="logo-link">
            <img 
              src={LOGO_URL} 
              alt="Acesion Distributors" 
              className="h-12 w-auto"
            />
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`nav-${link.label.toLowerCase().replace(" ", "-")}`}
                className="text-sm font-medium text-[#4B5563] hover:text-[#0047AB] transition-colors"
              >
                {link.label}
              </a>
            ))}
            <a
              href="#contact"
              data-testid="nav-get-started"
              className="px-5 py-2.5 bg-[#0047AB] text-white text-sm font-semibold rounded-md hover:bg-[#003080] transition-all hover:-translate-y-0.5 hover:shadow-lg"
            >
              Get Started
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <List size={24} />}
          </button>
        </nav>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              data-testid="mobile-menu"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-white border-t border-[#E5E7EB]"
            >
              <div className="py-4 space-y-2">
                {navLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block px-4 py-2 text-[#4B5563] hover:text-[#0047AB] hover:bg-[#F3F4F6] transition-colors"
                  >
                    {link.label}
                  </a>
                ))}
                <div className="px-4 pt-2">
                  <a
                    href="#contact"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block w-full px-5 py-2.5 bg-[#0047AB] text-white text-center font-semibold rounded-md"
                  >
                    Get Started
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

// Hero Section
const Hero = () => {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1766793110924-98e05b48eadc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjB3YXJlaG91c2UlMjBsb2dpc3RpY3N8ZW58MHx8fHwxNzc0OTMxNjIwfDA&ixlib=rb-4.1.0&q=85"
          alt="Modern warehouse"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/90 via-[#0A0A0A]/70 to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 pt-32 pb-20">
        <div className="max-w-2xl">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="uppercase text-xs tracking-[0.2em] font-bold text-[#FACC15] mb-6"
          >
            Distribution as a Service
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-none text-white mb-6 font-['Cabinet_Grotesk']"
          >
            Your Trusted FMCG Distribution Partner in{" "}
            <span className="text-[#FACC15]">Mumbai</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-lg text-gray-300 mb-8 leading-relaxed"
          >
            Acesion Distributors provides end-to-end distribution services on a
            flexible subscription model. From our warehouse in Badlapur to
            retailers across Mumbai, we ensure your products reach every shelf.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <a
              href="#contact"
              data-testid="hero-cta-primary"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#0047AB] text-white font-semibold rounded-md hover:bg-[#003080] transition-all hover:-translate-y-0.5 hover:shadow-xl"
            >
              Start Your Subscription
              <ArrowRight size={20} weight="bold" />
            </a>
            <a
              href="#services"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-white/30 text-white font-semibold rounded-md hover:bg-white/10 transition-all"
            >
              Explore Services
            </a>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="grid grid-cols-3 gap-8 mt-16 pt-8 border-t border-white/20"
          >
            {[
              { value: "500+", label: "Retailers" },
              { value: "50k+", label: "SKUs Handled" },
              { value: "98%", label: "On-Time Delivery" },
            ].map((stat) => (
              <div key={stat.label}>
                <p className="text-2xl sm:text-3xl font-bold text-[#FACC15] font-['Cabinet_Grotesk']">
                  {stat.value}
                </p>
                <p className="text-sm text-gray-400">{stat.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-1.5 h-1.5 bg-white rounded-full"
          />
        </div>
      </motion.div>
    </section>
  );
};

// Services Section (Bento Grid)
const Services = () => {
  const services = [
    {
      icon: Warehouse,
      title: "Warehousing",
      description:
        "State-of-the-art warehouse facility in Badlapur MIDC with climate control and real-time inventory tracking.",
      large: true,
    },
    {
      icon: Truck,
      title: "Last-Mile Delivery",
      description:
        "Reliable delivery network covering all of Mumbai's retail points.",
    },
    {
      icon: ChartLineUp,
      title: "Inventory Management",
      description:
        "Advanced inventory systems for optimal stock levels and zero stockouts.",
    },
    {
      icon: Users,
      title: "Retail Relationships",
      description:
        "Strong network of 500+ retail partners across Mumbai.",
    },
    {
      icon: ShieldCheck,
      title: "Quality Assurance",
      description:
        "Rigorous quality checks at every stage of the distribution process.",
      large: true,
    },
    {
      icon: Lightning,
      title: "Express Distribution",
      description: "Same-day and next-day delivery options for urgent requirements.",
    },
  ];

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 md:py-32 bg-[#F3F4F6]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="uppercase text-xs tracking-[0.2em] font-bold text-[#4B5563] mb-4">
            What We Do
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0A0A0A] font-['Cabinet_Grotesk']">
            Complete Distribution Solutions
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              data-testid={`service-card-${index}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`group bg-white p-8 border border-[#E5E7EB] hover:border-[#0047AB]/50 transition-all hover:-translate-y-1 hover:shadow-lg ${
                service.large ? "md:col-span-2 lg:col-span-1" : ""
              }`}
            >
              <div className="w-12 h-12 bg-[#0047AB]/10 rounded-md flex items-center justify-center mb-6 group-hover:bg-[#0047AB] transition-colors">
                <service.icon
                  size={24}
                  weight="duotone"
                  className="text-[#0047AB] group-hover:text-white transition-colors"
                />
              </div>
              <h3 className="text-xl font-bold mb-3 font-['Cabinet_Grotesk']">
                {service.title}
              </h3>
              <p className="text-[#4B5563] leading-relaxed">
                {service.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Why Choose Us Section
const WhyUs = () => {
  const reasons = [
    {
      number: "01",
      title: "Subscription-Based Model",
      description:
        "Flexible monthly plans that scale with your business. No long-term commitments, pay for what you use.",
    },
    {
      number: "02",
      title: "Strategic Location",
      description:
        "HQ at Peninsula Business Park, Lower Parel and warehouse in Badlapur MIDC provide optimal coverage across Mumbai and MMR.",
    },
    {
      number: "03",
      title: "Technology-Driven",
      description:
        "Real-time tracking, automated reporting, and data analytics to optimize your distribution.",
    },
    {
      number: "04",
      title: "FMCG Expertise",
      description:
        "Years of experience handling fast-moving consumer goods with proper storage and handling.",
    },
  ];

  return (
    <section
      id="why-us"
      data-testid="why-us-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <p className="uppercase text-xs tracking-[0.2em] font-bold text-[#4B5563] mb-4">
              Why Choose Us
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0A0A0A] mb-6 font-['Cabinet_Grotesk']">
              Distribution Partner You Can Rely On
            </h2>
            <p className="text-[#4B5563] leading-relaxed mb-8">
              Acesion Distributors brings decades of combined experience in FMCG
              distribution. Our subscription model offers flexibility that
              traditional distributors can't match.
            </p>
            <img
              src="https://images.pexels.com/photos/1366594/pexels-photo-1366594.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="FMCG Retail"
              className="w-full h-64 object-cover"
            />
          </motion.div>

          <div className="space-y-6">
            {reasons.map((reason, index) => (
              <motion.div
                key={reason.number}
                data-testid={`reason-${index}`}
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group p-6 border border-[#E5E7EB] hover:border-[#0047AB]/50 transition-all hover:shadow-md"
              >
                <div className="flex gap-4">
                  <span className="text-3xl font-black text-[#0047AB]/20 group-hover:text-[#0047AB]/40 transition-colors font-['Cabinet_Grotesk']">
                    {reason.number}
                  </span>
                  <div>
                    <h3 className="text-lg font-bold mb-2 font-['Cabinet_Grotesk']">
                      {reason.title}
                    </h3>
                    <p className="text-[#4B5563] text-sm leading-relaxed">
                      {reason.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Pricing Section
const Pricing = () => {
  const plans = [
    {
      name: "Starter",
      price: "25,000",
      period: "/month",
      description: "Perfect for small brands entering the market",
      features: [
        "Up to 50 SKUs",
        "Weekly delivery schedule",
        "Basic inventory reports",
        "Email support",
        "100 retail points coverage",
      ],
      highlighted: false,
    },
    {
      name: "Growth",
      price: "75,000",
      period: "/month",
      description: "For established brands scaling distribution",
      features: [
        "Up to 200 SKUs",
        "3x weekly delivery",
        "Real-time inventory dashboard",
        "Dedicated account manager",
        "300 retail points coverage",
        "Priority support",
      ],
      highlighted: true,
    },
    {
      name: "Enterprise",
      price: "1,50,000",
      period: "/month",
      description: "Full-scale distribution partnership",
      features: [
        "Up to 500 SKUs",
        "Daily delivery options",
        "Custom analytics & BI",
        "On-site support team",
        "Full Mumbai coverage",
        "SLA guarantees",
      ],
      highlighted: false,
    },
    {
      name: "Tailored",
      price: "Custom",
      period: "",
      description: "Bespoke solutions for unique requirements",
      features: [
        "Unlimited SKUs",
        "Custom delivery schedules",
        "White-label reporting",
        "Dedicated fleet options",
        "Pan-India expansion support",
        "Revenue sharing models",
      ],
      highlighted: false,
    },
  ];

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="py-24 md:py-32 bg-[#0A0A0A]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="uppercase text-xs tracking-[0.2em] font-bold text-[#FACC15] mb-4">
            Pricing
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white font-['Cabinet_Grotesk']">
            Subscription Plans
          </h2>
          <p className="mt-4 text-gray-400 max-w-2xl mx-auto">
            Flexible plans designed for businesses of all sizes. No hidden fees,
            scale up or down anytime.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.name}
              data-testid={`pricing-card-${plan.name.toLowerCase()}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`relative p-6 border ${
                plan.highlighted
                  ? "border-[#FACC15] bg-white"
                  : "border-gray-800 bg-[#0A0A0A]"
              }`}
            >
              {plan.highlighted && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#FACC15] text-[#0A0A0A] text-xs font-bold uppercase tracking-wider">
                  Most Popular
                </div>
              )}
              <h3
                className={`text-lg font-bold mb-2 font-['Cabinet_Grotesk'] ${
                  plan.highlighted ? "text-[#0A0A0A]" : "text-white"
                }`}
              >
                {plan.name}
              </h3>
              <p
                className={`text-xs mb-4 ${
                  plan.highlighted ? "text-[#4B5563]" : "text-gray-400"
                }`}
              >
                {plan.description}
              </p>
              <div className="mb-4">
                <span
                  className={`text-3xl font-black font-['Cabinet_Grotesk'] ${
                    plan.highlighted ? "text-[#0047AB]" : "text-[#FACC15]"
                  }`}
                >
                  {plan.price === "Custom" ? "" : "₹"}
                  {plan.price}
                </span>
                <span
                  className={`text-sm ${
                    plan.highlighted ? "text-[#4B5563]" : "text-gray-400"
                  }`}
                >
                  {plan.period}
                </span>
              </div>
              <ul className="space-y-2 mb-6">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2">
                    <CheckCircle
                      size={16}
                      weight="fill"
                      className={`mt-0.5 flex-shrink-0 ${
                        plan.highlighted ? "text-[#0047AB]" : "text-[#FACC15]"
                      }`}
                    />
                    <span
                      className={`text-xs ${
                        plan.highlighted ? "text-[#4B5563]" : "text-gray-300"
                      }`}
                    >
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>
              <a
                href="#contact"
                data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                className={`block w-full py-2.5 text-center text-sm font-semibold transition-all hover:-translate-y-0.5 ${
                  plan.highlighted
                    ? "bg-[#0047AB] text-white hover:bg-[#003080]"
                    : "border border-white/30 text-white hover:bg-white/10"
                }`}
              >
                {plan.price === "Custom" ? "Contact Sales" : "Get Started"}
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Coverage Section
const Coverage = () => {
  return (
    <section
      id="coverage"
      data-testid="coverage-section"
      className="relative py-24 md:py-32 overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1653299311171-31939b3b84b0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxtdW1iYWklMjBza3lsaW5lJTIwd2lkZXxlbnwwfHx8fDE3NzQ5MzE2MjF8MA&ixlib=rb-4.1.0&q=85"
          alt="Mumbai Skyline"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0047AB]/90" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <p className="uppercase text-xs tracking-[0.2em] font-bold text-[#FACC15] mb-4">
              Our Coverage
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-6 font-['Cabinet_Grotesk']">
              Strategic Presence Across Mumbai Metropolitan Region
            </h2>
            <p className="text-white/80 leading-relaxed mb-8">
              With our headquarters at Peninsula Business Park in Lower Parel
              and a fully-equipped warehouse strategically located in Badlapur MIDC,
              we provide seamless coverage across the entire MMR region.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4 p-4 bg-white/10 backdrop-blur-sm">
                <div className="w-10 h-10 bg-[#FACC15] rounded flex items-center justify-center flex-shrink-0">
                  <Buildings size={20} weight="fill" className="text-[#0A0A0A]" />
                </div>
                <div>
                  <h4 className="font-bold text-white mb-1">Head Office</h4>
                  <p className="text-white/70 text-sm">
                    1102, 11th Floor, Tower B, Peninsula Business Park,
                    Ganpatrao Kadam Marg (S.B. Road), Lower Parel (West), Mumbai 400013
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-white/10 backdrop-blur-sm">
                <div className="w-10 h-10 bg-[#FACC15] rounded flex items-center justify-center flex-shrink-0">
                  <Warehouse size={20} weight="fill" className="text-[#0A0A0A]" />
                </div>
                <div>
                  <h4 className="font-bold text-white mb-1">Warehouse</h4>
                  <p className="text-white/70 text-sm">
                    19 Shivsundar, Kulgaon Badlapur, MIDC, Badlapur (E),
                    Dist. Thane, Tal. Ambernath
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="bg-white/10 backdrop-blur-sm p-8 border border-white/20">
              <h3 className="text-xl font-bold text-white mb-6 font-['Cabinet_Grotesk']">
                Coverage Areas
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {[
                  "South Mumbai",
                  "Western Suburbs",
                  "Central Mumbai",
                  "Eastern Suburbs",
                  "Navi Mumbai",
                  "Thane",
                  "Kalyan-Dombivli",
                  "Badlapur-Ambernath",
                ].map((area) => (
                  <div key={area} className="flex items-center gap-2">
                    <MapPin size={16} className="text-[#FACC15]" />
                    <span className="text-white/90 text-sm">{area}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const testimonials = [
    {
      quote:
        "Acesion Distributors transformed our distribution in Mumbai. Their subscription model gave us the flexibility to scale during peak seasons without long-term commitments. Our retail presence grew 3x in just 6 months.",
      author: "Vikram Desai",
      role: "Director, Desai Food Products",
      image:
        "https://images.unsplash.com/photo-1758599543154-76ec1c4257df?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHBvcnRyYWl0fGVufDB8fHx8MTc3NDkzMTYzOHww&ixlib=rb-4.1.0&q=85",
    },
    {
      quote:
        "As a growing snacks manufacturer from Thane, we needed a reliable distribution partner. Acesion's warehouse in Badlapur is perfectly located, and their on-time delivery rate has been exceptional.",
      author: "Sunita Patil",
      role: "Founder, Patil Namkeen Industries",
      image:
        "https://images.unsplash.com/photo-1581065178047-8ee15951ede6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHBvcnRyYWl0fGVufDB8fHx8MTc3NDkzMTYzOHww&ixlib=rb-4.1.0&q=85",
    },
    {
      quote:
        "We switched from a traditional distributor to Acesion's subscription model and haven't looked back. The real-time inventory tracking and dedicated support have helped us reduce stockouts by 90%.",
      author: "Rajesh Sharma",
      role: "MD, Sharma Home Care Products",
      image:
        "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHBvcnRyYWl0fGVufDB8fHx8MTc3NDkzMTYzOHww&ixlib=rb-4.1.0&q=85",
    },
    {
      quote:
        "Acesion's team understands the FMCG business inside out. Their advice on SKU rationalization and market coverage has been invaluable. A true partner, not just a distributor.",
      author: "Meera Kulkarni",
      role: "CEO, Kulkarni Spices & Masala",
      image:
        "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHw1fHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHBvcnRyYWl0fGVufDB8fHx8MTc3NDkzMTYzOHww&ixlib=rb-4.1.0&q=85",
    },
  ];

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex(
      (prev) => (prev - 1 + testimonials.length) % testimonials.length
    );
  };

  return (
    <section
      data-testid="testimonials-section"
      className="py-24 md:py-32 bg-[#F3F4F6]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="uppercase text-xs tracking-[0.2em] font-bold text-[#4B5563] mb-4">
            Testimonials
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0A0A0A] font-['Cabinet_Grotesk']">
            What Our Clients Say
          </h2>
        </motion.div>

        <div className="relative max-w-4xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              className="bg-white p-8 md:p-12 border border-[#E5E7EB]"
            >
              <div className="flex flex-col md:flex-row gap-8 items-center">
                <img
                  src={testimonials[currentIndex].image}
                  alt={testimonials[currentIndex].author}
                  className="w-24 h-24 rounded-full object-cover border-4 border-[#0047AB]/20"
                />
                <div>
                  <p className="text-lg md:text-xl text-[#0A0A0A] leading-relaxed mb-6 italic">
                    "{testimonials[currentIndex].quote}"
                  </p>
                  <div>
                    <p className="font-bold text-[#0A0A0A] font-['Cabinet_Grotesk']">
                      {testimonials[currentIndex].author}
                    </p>
                    <p className="text-[#4B5563] text-sm">
                      {testimonials[currentIndex].role}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex justify-center gap-4 mt-8">
            <button
              data-testid="testimonial-prev"
              onClick={prevTestimonial}
              className="w-12 h-12 border border-[#E5E7EB] flex items-center justify-center hover:bg-[#0047AB] hover:text-white hover:border-[#0047AB] transition-all"
            >
              <CaretLeft size={20} />
            </button>
            {testimonials.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`w-3 h-3 rounded-full transition-all ${
                  idx === currentIndex ? "bg-[#0047AB]" : "bg-[#E5E7EB]"
                }`}
              />
            ))}
            <button
              data-testid="testimonial-next"
              onClick={nextTestimonial}
              className="w-12 h-12 border border-[#E5E7EB] flex items-center justify-center hover:bg-[#0047AB] hover:text-white hover:border-[#0047AB] transition-all"
            >
              <CaretRight size={20} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Form Section
const ContactForm = () => {
  const [formData, setFormData] = useState({
    companyName: "",
    contactPerson: "",
    email: "",
    phone: "",
    productCategory: "",
    estimatedSKUs: "",
    preferredPlan: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);
  setSubmitStatus(null);

  try {
    const fd = new FormData();

    fd.append("companyName", formData.companyName);
    fd.append("contactPerson", formData.contactPerson);
    fd.append("email", formData.email);
    fd.append("phone", formData.phone);
    fd.append("productCategory", formData.productCategory);
    fd.append("estimatedSKUs", formData.estimatedSKUs);
    fd.append("preferredPlan", formData.preferredPlan);
    fd.append("message", formData.message);

    const response = await axios.post(
      "https://sweet-moon-f501.prashants-account.workers.dev/contact",
      fd
    );

    if (response.data.success) {
      setSubmitStatus("success");

      setFormData({
        companyName: "",
        contactPerson: "",
        email: "",
        phone: "",
        productCategory: "",
        estimatedSKUs: "",
        preferredPlan: "",
        message: "",
      });
    } else {
      setSubmitStatus("error");
    }
  } catch (error) {
    console.error(error.response?.data || error.message);
    setSubmitStatus("error");
  } finally {
    setIsSubmitting(false);
  }
};
      
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-24">
          {/* Left Column - Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <p className="uppercase text-xs tracking-[0.2em] font-bold text-[#4B5563] mb-4">
              Get In Touch
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0A0A0A] mb-6 font-['Cabinet_Grotesk']">
              Start Your Distribution Journey
            </h2>
            <p className="text-[#4B5563] leading-relaxed mb-8">
              Ready to scale your distribution in Mumbai? Fill out the form and
              our team will get back to you within 24 hours with a customized
              plan for your business.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#0047AB]/10 rounded-md flex items-center justify-center flex-shrink-0">
                  <Buildings size={24} className="text-[#0047AB]" />
                </div>
                <div>
                  <p className="font-semibold text-[#0A0A0A]">Head Office</p>
                  <p className="text-[#4B5563] text-sm">
                    1102, 11th Floor, Tower B, Peninsula Business Park,<br />
                    Ganpatrao Kadam Marg (S.B. Road),<br />
                    Lower Parel (West), Mumbai 400013
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#0047AB]/10 rounded-md flex items-center justify-center flex-shrink-0">
                  <Warehouse size={24} className="text-[#0047AB]" />
                </div>
                <div>
                  <p className="font-semibold text-[#0A0A0A]">Warehouse</p>
                  <p className="text-[#4B5563] text-sm">
                    19 Shivsundar, Kulgaon Badlapur, MIDC,<br />
                    Badlapur (E), Dist. Thane, Tal. Ambernath
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#0047AB]/10 rounded-md flex items-center justify-center">
                  <Globe size={24} className="text-[#0047AB]" />
                </div>
                <div>
                  <p className="font-semibold text-[#0A0A0A]">Website</p>
                  <p className="text-[#4B5563] text-sm">www.acesion.co</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#0047AB]/10 rounded-md flex items-center justify-center">
                  <Clock size={24} className="text-[#0047AB]" />
                </div>
                <div>
                  <p className="font-semibold text-[#0A0A0A]">Business Hours</p>
                  <p className="text-[#4B5563] text-sm">Mon - Sat: 9AM - 6PM</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column - Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="bg-[#F3F4F6] p-8 border border-[#E5E7EB]"
            >
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-[#0A0A0A] mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    required
                    data-testid="input-company-name"
                    className="w-full px-4 py-3 border border-[#E5E7EB] bg-white focus:border-[#0047AB] focus:ring-2 focus:ring-[#0047AB]/20 outline-none transition-all"
                    placeholder="Your Company"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#0A0A0A] mb-2">
                    Contact Person *
                  </label>
                  <input
                    type="text"
                    name="contactPerson"
                    value={formData.contactPerson}
                    onChange={handleChange}
                    required
                    data-testid="input-contact-person"
                    className="w-full px-4 py-3 border border-[#E5E7EB] bg-white focus:border-[#0047AB] focus:ring-2 focus:ring-[#0047AB]/20 outline-none transition-all"
                    placeholder="Full Name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#0A0A0A] mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="input-email"
                    className="w-full px-4 py-3 border border-[#E5E7EB] bg-white focus:border-[#0047AB] focus:ring-2 focus:ring-[#0047AB]/20 outline-none transition-all"
                    placeholder="email@company.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#0A0A0A] mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    data-testid="input-phone"
                    className="w-full px-4 py-3 border border-[#E5E7EB] bg-white focus:border-[#0047AB] focus:ring-2 focus:ring-[#0047AB]/20 outline-none transition-all"
                    placeholder="+91 XXXXX XXXXX"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#0A0A0A] mb-2">
                    Product Category
                  </label>
                  <select
                    name="productCategory"
                    value={formData.productCategory}
                    onChange={handleChange}
                    data-testid="select-product-category"
                    className="w-full px-4 py-3 border border-[#E5E7EB] bg-white focus:border-[#0047AB] focus:ring-2 focus:ring-[#0047AB]/20 outline-none transition-all"
                  >
                    <option value="">Select Category</option>
                    <option value="food-beverages">Food & Beverages</option>
                    <option value="personal-care">Personal Care</option>
                    <option value="home-care">Home Care</option>
                    <option value="health-wellness">Health & Wellness</option>
                    <option value="snacks-namkeen">Snacks & Namkeen</option>
                    <option value="spices-masala">Spices & Masala</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#0A0A0A] mb-2">
                    Estimated SKUs
                  </label>
                  <select
                    name="estimatedSKUs"
                    value={formData.estimatedSKUs}
                    onChange={handleChange}
                    data-testid="select-estimated-skus"
                    className="w-full px-4 py-3 border border-[#E5E7EB] bg-white focus:border-[#0047AB] focus:ring-2 focus:ring-[#0047AB]/20 outline-none transition-all"
                  >
                    <option value="">Select Range</option>
                    <option value="1-50">1 - 50 SKUs</option>
                    <option value="51-100">51 - 100 SKUs</option>
                    <option value="101-200">101 - 200 SKUs</option>
                    <option value="201-500">201 - 500 SKUs</option>
                    <option value="500+">500+ SKUs</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-[#0A0A0A] mb-2">
                    Preferred Subscription Plan
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {["Starter", "Growth", "Enterprise", "Tailored"].map((plan) => (
                      <label
                        key={plan}
                        data-testid={`radio-plan-${plan.toLowerCase()}`}
                        className={`flex items-center justify-center p-3 border cursor-pointer transition-all ${
                          formData.preferredPlan === plan
                            ? "border-[#0047AB] bg-[#0047AB]/10"
                            : "border-[#E5E7EB] bg-white hover:border-[#0047AB]/50"
                        }`}
                      >
                        <input
                          type="radio"
                          name="preferredPlan"
                          value={plan}
                          checked={formData.preferredPlan === plan}
                          onChange={handleChange}
                          className="sr-only"
                        />
                        <span
                          className={`font-semibold text-sm ${
                            formData.preferredPlan === plan
                              ? "text-[#0047AB]"
                              : "text-[#4B5563]"
                          }`}
                        >
                          {plan}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-[#0A0A0A] mb-2">
                    Additional Requirements
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    data-testid="textarea-message"
                    className="w-full px-4 py-3 border border-[#E5E7EB] bg-white focus:border-[#0047AB] focus:ring-2 focus:ring-[#0047AB]/20 outline-none transition-all resize-none"
                    placeholder="Tell us about your distribution needs, current challenges, or any specific requirements..."
                  />
                </div>
              </div>

              {submitStatus === "success" && (
                <div
                  data-testid="form-success-message"
                  className="mt-6 p-4 bg-green-50 border border-green-200 text-green-800"
                >
                  Thank you! Your inquiry has been submitted. Our team will
                  contact you within 24 hours.
                </div>
              )}

              {submitStatus === "error" && (
                <div
                  data-testid="form-error-message"
                  className="mt-6 p-4 bg-red-50 border border-red-200 text-red-800"
                >
                  Oops! Something went wrong. Please try again or contact us
                  directly.
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                data-testid="contact-submit-btn"
                className="mt-6 w-full py-4 bg-[#0047AB] text-white font-semibold hover:bg-[#003080] transition-all hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  "Submitting..."
                ) : (
                  <>
                    Submit Inquiry
                    <ArrowRight size={20} weight="bold" />
                  </>
                )}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-[#0A0A0A] py-16">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <img 
                src={LOGO_URL} 
                alt="Acesion Distributors" 
                className="h-12 w-auto"
              />
            </div>
            <p className="text-gray-400 leading-relaxed max-w-md mb-6">
              Your trusted partner for FMCG distribution in Mumbai. We provide
              end-to-end distribution services on flexible subscription plans.
            </p>
            <div className="flex gap-4">
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-linkedin"
                className="w-10 h-10 border border-gray-700 flex items-center justify-center hover:bg-[#0047AB] hover:border-[#0047AB] transition-all"
              >
                <LinkedinLogo size={20} className="text-white" />
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-twitter"
                className="w-10 h-10 border border-gray-700 flex items-center justify-center hover:bg-[#0047AB] hover:border-[#0047AB] transition-all"
              >
                <TwitterLogo size={20} className="text-white" />
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-instagram"
                className="w-10 h-10 border border-gray-700 flex items-center justify-center hover:bg-[#0047AB] hover:border-[#0047AB] transition-all"
              >
                <InstagramLogo size={20} className="text-white" />
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-white font-bold mb-4 font-['Cabinet_Grotesk']">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {[
                { href: "#services", label: "Services" },
                { href: "#pricing", label: "Pricing" },
                { href: "#coverage", label: "Coverage" },
                { href: "#contact", label: "Contact" },
              ].map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className="text-gray-400 hover:text-[#FACC15] transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-bold mb-4 font-['Cabinet_Grotesk']">
              Locations
            </h4>
            <ul className="space-y-3">
              <li className="text-gray-400 text-sm">
                <span className="text-[#FACC15] font-semibold">HQ:</span><br />
                Peninsula Business Park,<br />
                Lower Parel, Mumbai 400013
              </li>
              <li className="text-gray-400 text-sm">
                <span className="text-[#FACC15] font-semibold">Warehouse:</span><br />
                MIDC, Badlapur (E),<br />
                Dist. Thane
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm">
            © 2024 Acesion Distributors. All rights reserved.
          </p>
          <p className="text-gray-500 text-sm">
            <a href="https://www.acesion.co" className="hover:text-[#FACC15] transition-colors">
              www.acesion.co
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

// WhatsApp Floating Button Component
const WhatsAppButton = () => {
  const [isHovered, setIsHovered] = useState(false);
  
  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;
  
  return (
    <motion.div
      data-testid="whatsapp-button"
      className="fixed bottom-6 right-6 z-50"
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ delay: 1, type: "spring", stiffness: 200 }}
    >
      <a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="group flex items-center"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Tooltip */}
        <AnimatePresence>
          {isHovered && (
            <motion.div
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              className="mr-3 px-4 py-2 bg-white rounded-lg shadow-lg border border-gray-200"
            >
              <p className="text-sm font-medium text-gray-800 whitespace-nowrap">
                Chat with us on WhatsApp
              </p>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Button */}
        <motion.div
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className="w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
        >
          <WhatsappLogo size={28} weight="fill" className="text-white" />
        </motion.div>
      </a>
      
      {/* Pulse animation */}
      <div className="absolute inset-0 -z-10">
        <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-25"></span>
      </div>
    </motion.div>
  );
};

// Main App Component
function App() {
  return (
    <div className="App" data-testid="app-container">
      <Navigation />
      <main>
        <Hero />
        <Services />
        <WhyUs />
        <Pricing />
        <Coverage />
        <Testimonials />
        <ContactForm />
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}

export default App;
